import { Group } from '@/components/_common';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import StatusTag from '@/components/_common/StatusTag';
import {
  AccountReceivableFormValues,
  AccountReceivableSituationColor,
  AccountRecivableSituationType,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { useAccountReceivableContext } from '@/pages/FinancialPages/BillToReceivePage/ReleasesPage/ReleasesDetailPage';
import { Row, Col, Space, Typography, Button, Form, Popover } from 'antd';
import { AlertCircle, ChevronLeft, ChevronRight } from 'react-feather';
import { FormattedMessage } from 'react-intl';
import AccountReceivableHeaderTag from './AccountReceivableHeaderTag/index.';
import BankConciliationPopoverDetails from '@/components/_common/BankConciliation/BankConciliationPopoverDetails';

interface Props {
  hasDiffRate: boolean;
}

const AccountReceivableHeader: React.FC<Props> = ({ hasDiffRate }) => {
  const form = Form.useFormInstance<AccountReceivableFormValues>();
  const {
    accountReceivable,
    currentInstallment,
    totalInstallments,
    nextInstallment,
    prevInstallment,
  } = useAccountReceivableContext();
  const isLastComplement = totalInstallments - 1 === currentInstallment;
  const isFirstComplement = currentInstallment === 0;

  const accountCode = String(accountReceivable?.code).padStart(2, '0');
  const installmentIndex = String(currentInstallment + 1).padStart(2, '0');
  const installmentIndexTotal = String(totalInstallments).padStart(2, '0');

  const installment =
    accountReceivable?.installmentReceivables[currentInstallment];

  const statusColor = installment
    ? AccountReceivableSituationColor[installment.situation]
    : 'default';

  const statusLabel = installment
    ? AccountRecivableSituationType[installment.situation]
    : null;

  const conciliateInstallmentResponse =
    installment?.conciliateInstallmentResponse;

  return (
    <Row
      gutter={[16, 8]}
      align="middle"
      justify="space-between"
      style={{ marginBottom: '24px' }}
    >
      <Col>
        <Typography.Title level={4} id="status">
          <Group gap={8}>
            <FormattedMessage id="commons.entity.situation" />:
            <StatusTag
              color={statusColor}
              label={statusLabel || installment?.situation || ''}
              size="lg"
            />
            {hasDiffRate && (
              <Popover
                content={
                  <div style={{ maxWidth: 300 }}>
                    <span style={{ color: 'orange', fontWeight: 'bold' }}>
                      Atenção:
                    </span>{' '}
                    Parcelas foram geradas com tarifas diferentes. Para fazer o
                    ajuste, será necessário estornar todas as parcelas.
                  </div>
                }
              >
                <AlertCircle color="orange" fill="#ffe9c2" />
              </Popover>
            )}
            {!!conciliateInstallmentResponse && (
              <BankConciliationPopoverDetails
                conciliatedInstallment={conciliateInstallmentResponse}
              />
            )}
            <AccountReceivableHeaderTag />
          </Group>
        </Typography.Title>
      </Col>

      <Col>
        <Space align="center">
          <Button
            icon={
              <AntdFeatherIcon
                icon={<ChevronLeft cursor="pointer" />}
                size={14}
              />
            }
            onClick={() => {
              form.setFieldValue('updateTariff', false);
              prevInstallment();
            }}
            disabled={isFirstComplement}
            size="small"
          />

          <Typography.Title level={5} style={{ margin: 0 }}>
            {`${accountCode} - ${installmentIndex}/${installmentIndexTotal}`}
          </Typography.Title>

          <Button
            icon={
              <AntdFeatherIcon
                icon={<ChevronRight cursor="pointer" />}
                size={14}
              />
            }
            size="small"
            onClick={() => {
              form.setFieldValue('updateTariff', false);
              nextInstallment();
            }}
            disabled={isLastComplement}
          />
        </Space>
      </Col>
    </Row>
  );
};

export default AccountReceivableHeader;
